import { VaultObjectModalPortal } from "@/components/portal/VaultObjectModalPortal";
import { VaultObjectViewer } from "@/components/vault/viewer/VaultObjectViewer";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import { VaultObjectData } from "@/types/data-types";
import { processUploadFiles } from "@/utils/fileUtil";
import HUI from "@henge-inc/ui/v1_2";
import { motion } from "motion/react";
import { useCallback, useEffect, useRef } from "react";
import { useEscape } from "@/hooks/useEscape";
import useGlobalStore, {
  useGlobalActions,
} from "@/stores/zustand/useGlobalStore";
import { FilterBackgroundName } from "@/types/vault-object-filter-types";

export type LoaderData = {
  name: string;
  extension: string;
  url: string;
};
export type VaultObjectViewerData = VaultObjectData & { embed?: boolean };

export const VaultObjectViewerModal = () => {
  const { showPopupError } = useGlobalActions();

  const vaultData = useVaultStore((state) => state.vault);
  const vaultObjectUploadFileList = useVaultStore(
    (state) => state.vaultObjectUploadFileList,
  );
  const isViewerOpen = useVaultStore((state) => state.isViewerOpen);
  const loaderData = useVaultStore(
    (state) => state.vaultObjectViewerLoaderData,
  );
  const vaultObjectViewerData = useVaultStore(
    (state) => state.vaultObjectViewerData,
  );

  const currentPage = useVaultStore((state) => state.currentPage);
  const canvasStatus = useVaultStore((state) => state.canvasStatus);

  const { closeVaultObjectViewer, updatePreprocessedData } = useVaultStore(
    (state) => state.vaultStoreActions,
  );
  const onCloseModal = useCallback(() => {
    if (currentPage.startsWith("upload")) {
      if (confirm("업로드를 취소하시겠습니까?")) {
        closeVaultObjectViewer();
        return;
      }
    }

    closeVaultObjectViewer();
  }, [currentPage]);

  const rootScrollbar = useGlobalStore((state) => state.rootScrollbar);
  useEffect(() => {
    if (isViewerOpen) {
      rootScrollbar?.options({
        overflow: {
          x: "hidden",
          y: "hidden",
        },
      });
    } else {
      rootScrollbar?.options({
        overflow: {
          x: "hidden",
          y: "scroll",
        },
      });
    }
  }, [isViewerOpen]);

  useEscape(onCloseModal, { enabled: isViewerOpen });

  useEffect(() => {
    const target = ref.current;
    if (!target) return;

    if (isViewerOpen) {
      target.setAttribute("modal-opened", "");
    } else {
      target.removeAttribute("modal-opened");
    }

    return () => {
      target.removeAttribute("modal-opened");
    };
  }, [isViewerOpen]);

  useEffect(() => {
    if (!ref.current) return;

    ref.current.style.setProperty(
      "--vault-object-personal-colour",
      HUI.HColour.Indigo100().getColour().oklch.getCSS(),
    );
  }, []);

  // upload by files
  useEffect(() => {
    (async () => {
      if (!isViewerOpen) return;
      if (!vaultObjectUploadFileList) return;
      if (!vaultData) return;

      const files = processUploadFiles(
        vaultObjectUploadFileList,
        // splatExtensions,
      );

      if (files.accepted3DFiles.length === 0) {
        closeVaultObjectViewer();
        showPopupError([{ text: "지원하지 않는 3D 파일입니다." }]);
        return;
      } else if (files.accepted3DFiles.length > 1) {
        closeVaultObjectViewer();
        showPopupError([{ text: "한 번에 1개의 3D 파일만 올릴 수 있어요." }]);
        return;
      }
      const fileNameAndExtension = files.accepted3DFiles[0].name.split(".");
      if (fileNameAndExtension.length < 2) {
        closeVaultObjectViewer();
        showPopupError([{ text: "지원하지 않는 3D 파일입니다." }]);
        return;
      }

      const extension = (fileNameAndExtension.pop() as string).toLowerCase();
      const baseName = fileNameAndExtension.join("");

      /**
       * Preprocess Upload
       * */
      const tempFile = files.accepted3DFiles[0];
      const tempUrl = URL.createObjectURL(tempFile);

      updatePreprocessedData({
        uploadType: "raw",
        name: baseName,
        extension,
        file: tempFile,
        url: tempUrl,
      });
    })();
  }, [vaultObjectUploadFileList]);

  //================================================================

  const backgroundRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!backgroundRef.current) return;

    if (canvasStatus === "idle" || canvasStatus === "loading") {
      backgroundRef.current.style.removeProperty(
        "--vault-object-background-colour",
      );
    } else {
      if (
        vaultObjectViewerData?.filter?.background.type ===
        FilterBackgroundName.Color
      ) {
        backgroundRef.current.style.setProperty(
          "--vault-object-background-colour",
          `${vaultObjectViewerData.filter.background.hex}80`,
        );
      }
    }
  }, [canvasStatus]);
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <VaultObjectModalPortal>
      <motion.div
        ref={backgroundRef}
        className={"absolute inset-0 z-[1] transition-all"}
        style={{
          backgroundColor:
            "var(--vault-object-background-colour, rgba(255,255,255,0.5))",
          backdropFilter: "blur(16px)",
        }}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
      />
      <motion.div
        ref={ref}
        className={"modal"}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
      >
        <VaultObjectViewer
          loaderData={loaderData}
          onCloseModal={onCloseModal}
          vaultObjectViewerData={vaultObjectViewerData}
        />
      </motion.div>
    </VaultObjectModalPortal>
  );
};
